import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { format } from "date-fns";

export const useBookingForm = (navigate) => {
  const { t } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    package_id: "",
    package_name: "",
    event_type: "",
    event_date: "",
    guest_count: 50,
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    venue_address: "",
    special_requests: "",
    total_price: 0,
  });

  const fetchPackages = useCallback(async () => {
    try {
      const [packagesRes, blockedRes] = await Promise.all([
        axios.get(`${API}/packages`),
        axios.get(`${API}/availability/blocked-dates`),
      ]);
      setPackages(packagesRes.data);
      setBlockedDates(blockedRes.data.map((b) => b.date));
    } catch {
      toast.error(t("booking.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t, API]);

  const selectedPackage = packages.find((p) => p.id === formData.package_id);

  const handlePackageSelect = (pkg) => {
    setFormData((prev) => ({
      ...prev,
      package_id: pkg.id,
      package_name: pkg.name,
    }));
  };

  const handleGuestCountChange = (value) => {
    const count = parseInt(value) || 0;
    setFormData((prev) => ({ ...prev, guest_count: count }));
  };

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    setFormData((prev) => ({
      ...prev,
      event_date: selectedDate ? format(selectedDate, "yyyy-MM-dd") : "",
    }));
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, formData);
      toast.success(t("booking.toast.success"));
      navigate("/");
    } catch (error) {
      logger.error("Booking submission failed:", error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || t("booking.toast.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const isStep1Valid =
    Boolean(formData.event_type) &&
    Boolean(formData.event_date) &&
    formData.guest_count > 0;

  const isStep2Valid =
    Boolean(formData.customer_name) &&
    Boolean(formData.customer_email) &&
    Boolean(formData.customer_phone) &&
    Boolean(formData.venue_address) &&
    (formData.event_type !== "Other" || Boolean(formData.special_requests));

  const selectedPackageName = selectedPackage?.name || "";

  return {
    packages,
    blockedDates,
    loading,
    step,
    setStep,
    submitting,
    date,
    formData,
    selectedPackage,
    selectedPackageName,
    fetchPackages,
    handlePackageSelect,
    handleGuestCountChange,
    handleDateSelect,
    updateField,
    handleSubmit,
    isStep1Valid,
    isStep2Valid,
  };
};
